import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const MIGRATION_KEY = 'miko_studio_migration_done';

/**
 * When a Miko Studio user logs in on MusiKonzept for the first time,
 * run the Thinkific migration to import their courses/certificates
 * and update their profile to include the musikonzept platform.
 *
 * Important: Supabase auth callbacks must stay synchronous. We therefore
 * schedule the async migration outside onAuthStateChange to avoid auth deadlocks.
 */
export const useMikoStudioMigration = () => {
  const queryClient = useQueryClient();
  const migrationRunningRef = useRef(false);

  const runMigrationForUser = useCallback(async (userId: string) => {
    if (!userId || migrationRunningRef.current) return;

    // Skip if already migrated in this browser
    const alreadyDone = localStorage.getItem(MIGRATION_KEY);
    if (alreadyDone === userId) return;

    migrationRunningRef.current = true;

    try {
      // Check if user is a Miko Studio user without musikonzept platform
      const { data: profile } = await supabase
        .from('profiles')
        .select('source, platforms')
        .eq('user_id', userId)
        .maybeSingle();

      if (!profile) return;

      const platforms: string[] = Array.isArray(profile.platforms) ? profile.platforms : [];
      const isMikoStudioOnly = profile.source === 'miko_studio' && !platforms.includes('musikonzept');

      if (!isMikoStudioOnly) {
        // Mark as done so we don't check again
        localStorage.setItem(MIGRATION_KEY, userId);
        return;
      }

      console.log('[MikoStudioMigration] Detected Miko Studio user logging into MusiKonzept, starting migration...');

      // Step 1: Update profile to include musikonzept platform
      await supabase
        .from('profiles')
        .update({ platforms: [...platforms, 'musikonzept'] })
        .eq('user_id', userId);

      // Step 2: Enroll in Start-Zugang bundle
      try {
        await supabase.from('bundle_enrollments').insert({
          user_id: userId,
          bundle_id: 'a1b2c3d4-5678-90ab-cdef-1234567890ab',
        });
      } catch {
        // ON CONFLICT is fine
      }

      // Step 3: Run Thinkific migration
      const { data: migrationResult, error: migrationError } = await supabase.functions.invoke('migrate-thinkific-on-register');

      if (migrationError) {
        console.warn('[MikoStudioMigration] Migration error:', migrationError);
      } else if (migrationResult?.migrated && migrationResult.enrollments > 0) {
        console.log('[MikoStudioMigration] Migration successful:', migrationResult);
        toast.success('Willkommen bei MusiKonzept! 🎵', {
          description: `${migrationResult.enrollments} Kurs(e) und ${migrationResult.certificates || 0} Zertifikat(e) von deinem bisherigen Account importiert.`,
          duration: 8000,
        });
      }

      // Step 4: Check subscription status
      try {
        await supabase.functions.invoke('check-abo-subscription');
      } catch (e) {
        console.warn('[MikoStudioMigration] Subscription check failed:', e);
      }

      // Step 5: Invalidate caches
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['global-access'] }),
        queryClient.invalidateQueries({ queryKey: ['student-courses'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-courses'] }),
      ]);

      localStorage.setItem(MIGRATION_KEY, userId);
      console.log('[MikoStudioMigration] Migration complete');
    } catch (err) {
      console.error('[MikoStudioMigration] Error:', err);
    } finally {
      migrationRunningRef.current = false;
    }
  }, [queryClient]);

  useEffect(() => {
    let isMounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event !== 'SIGNED_IN' || !session?.user?.id) return;

      const signedInUserId = session.user.id;
      window.setTimeout(() => {
        if (!isMounted) return;
        void runMigrationForUser(signedInUserId);
      }, 0);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [runMigrationForUser]);
};
